import { useState, useEffect, useMemo } from "react";
import SectionBg from "../components/SectionBg";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Sparkles, Quote, Save, Edit3, ExternalLink } from "lucide-react";
import AdminEditWrapper from "../components/AdminEditWrapper";
import CalendarWidget from "../components/CalendarWidget";
import DashboardInfoCards from "../components/DashboardInfoCards";
import Marquee from "../components/Marquee";
import { HERO_IMAGES } from "../components/PageHero";
import QUOTES from "../data/quotes";

const TM_QUOTES = QUOTES.filter(q => q.c === "Trading" || q.c === "Mindset").slice(0, 30);
const QUOTE_COLORS = { Trading: "#2FD8A8", Mindset: "#64C8FF" };

function DailyQuote() {
  const quote = useMemo(() => {
    const now = new Date();
    const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000);
    return { ...TM_QUOTES[dayOfYear % TM_QUOTES.length], day: dayOfYear };
  }, []);
  const color = QUOTE_COLORS[quote.c] || "#2FD8A8";

  return (
    <div className="max-w-4xl mx-auto mb-12 animate-fadeIn stagger-1" data-testid="daily-quote">
      <div className="relative px-6 py-6 rounded-xl overflow-hidden" style={{ backgroundColor: `${color}08`, border: `1px solid ${color}30` }}>
        <Quote size={22} className="absolute top-4 left-4 opacity-30" style={{ color }} />
        <div className="pl-8 relative z-10">
          <p className="text-lg sm:text-xl italic leading-relaxed font-medium" style={{ color }} data-testid="daily-quote-text">
            &ldquo;{quote.q}&rdquo;
          </p>
          <div className="flex items-center gap-3 mt-3">
            <span className="text-[10px] font-bold uppercase px-2.5 py-1 rounded" style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}40`, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.08em" }} data-testid="daily-quote-category">
              {quote.c}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", content: "" });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/dashboard`);
      setContent(response.data);
    } catch (e) {
      toast.error("Errore nel caricamento dei contenuti");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ title: item.title || "", content: item.content });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", content: "" });
  };

  const saveEdit = async (id) => {
    try {
      const dataToSave = { ...editForm };
      // Convert YouTube URL if it's a video
      if (editForm.content && (editForm.content.includes('youtube') || editForm.content.includes('youtu.be'))) {
        dataToSave.content = convertToEmbedUrl(editForm.content);
      }
      await axios.put(`${API}/content/${id}`, dataToSave);
      toast.success("Contenuto aggiornato");
      fetchContent();
      cancelEdit();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const textContent = content.find(c => c.content_type === "text");

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center" data-testid="dashboard-loading">
        <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard-page">
      {/* Full-width photo hero */}
      <div className="dash-hero">
        <div className="page-hero-img" style={{ backgroundImage: `url(${HERO_IMAGES.dashboard})` }} />
        <div className="page-hero-overlay" />
        <div className="container relative h-full flex flex-col justify-end pb-12 sm:pb-16 animate-fadeIn">
          <div className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 sm:mb-6" style={{ backgroundColor: "rgba(47, 216, 168, 0.1)", border: "1px solid rgba(47, 216, 168, 0.3)" }}>
            <Sparkles size={18} style={{ color: "var(--mm-gold)" }} />
            <span className="text-sm sm:text-base" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>CENTRO DI CONTROLLO</span>
          </div>
          
          {textContent && (
            <AdminEditWrapper
              isAdmin={isAdmin}
              isEditing={editingId === textContent.id}
              onEdit={() => startEdit(textContent)}
              onSave={() => saveEdit(textContent.id)}
              onCancel={cancelEdit}
            >
              {editingId === textContent.id ? (
                <div className="space-y-4 max-w-2xl mx-auto">
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="admin-input text-center text-2xl"
                    placeholder="Titolo"
                    data-testid="edit-title-input"
                  />
                  <textarea
                    value={editForm.content}
                    onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                    className="admin-textarea text-center"
                    placeholder="Descrizione"
                    data-testid="edit-content-textarea"
                  />
                </div>
              ) : (
                <>
                  <h1 
                    className="display-title shimmer-gold mb-4 sm:mb-6"
                    style={{ fontSize: "clamp(2.2rem, 5.5vw, 4.5rem)" }}
                    data-testid="dashboard-title"
                  >
                    {textContent.title}
                  </h1>
                  <p 
                    className="text-base sm:text-lg max-w-2xl leading-relaxed"
                    style={{ color: "rgba(255,255,255,0.8)" }}
                    data-testid="dashboard-description"
                  >
                    {textContent.content}
                  </p>
                </>
              )}
            </AdminEditWrapper>
          )}
        </div>
      </div>

      <SectionBg type="trading" />
      <div className="container py-8 sm:py-10 relative">
        {/* Frase motivazionale del giorno (Trading/Mindset) */}
        <DailyQuote />

        {/* Admin-editable Link Button */}
        <DashboardLinkButton isAdmin={isAdmin} />

        {/* Calendar Widget - Above video */}
        <div className="max-w-5xl mx-auto mb-12 animate-fadeIn stagger-1">
          <CalendarWidget />
        </div>

        {/* Info Cards Grid (editable, open popup) */}
        <DashboardInfoCards isAdmin={isAdmin} />
      </div>

      {/* Scrolling brand marquee */}
      <div className="relative mt-4 py-6">
        <Marquee />
      </div>
    </div>
  );
}


function DashboardLinkButton({ isAdmin }) {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ text: "", url: "" });

  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(res => {
        setText(res.data.dashboard_button_text || "");
        setUrl(res.data.dashboard_button_url || "");
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async () => {
    if (!form.text.trim()) { toast.error("Inserisci il testo del bottone"); return; }
    try {
      await axios.put(`${API}/settings/dashboard_button_text`, { value: form.text.trim() });
      await axios.put(`${API}/settings/dashboard_button_url`, { value: form.url.trim() });
      setText(form.text.trim());
      setUrl(form.url.trim());
      setEditing(false);
      toast.success("Bottone aggiornato");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  if (!loaded) return null;
  if (!text && !isAdmin) return null;

  if (editing) {
    return (
      <div className="max-w-xl mx-auto mb-8 glass-card p-5 space-y-3 animate-fadeIn" data-testid="dashboard-button-edit-form">
        <h3 className="text-lg font-semibold text-center" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>
          Modifica Bottone
        </h3>
        <input
          type="text"
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          className="admin-input"
          placeholder="Testo del bottone"
          data-testid="dashboard-button-text-input"
        />
        <input
          type="text"
          value={form.url}
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          className="admin-input"
          placeholder="Link (es. https://...)"
          data-testid="dashboard-button-url-input"
        />
        <div className="flex gap-3">
          <button onClick={save} className="btn-primary flex-1 flex items-center justify-center gap-2 py-2 text-sm" data-testid="dashboard-button-save-btn">
            <Save size={16} /> Salva
          </button>
          <button onClick={() => setEditing(false)} className="btn-secondary flex-1 py-2 text-sm" data-testid="dashboard-button-cancel-btn">
            Annulla
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mb-12 animate-fadeIn relative group" data-testid="dashboard-link-button-wrapper">
      <a
        href={url || undefined}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => { if (!url) e.preventDefault(); }}
        className="flex items-center justify-center gap-3 w-full py-4 px-6 rounded-lg font-bold text-lg sm:text-xl uppercase transition-all duration-300 hover:scale-[1.01] active:scale-[0.99]"
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          letterSpacing: "0.08em",
          color: "#0A0A0A",
          background: "linear-gradient(135deg, #2FD8A8 0%, #1FB489 100%)",
          boxShadow: "0 4px 24px rgba(47, 216, 168, 0.25)",
          textDecoration: "none"
        }}
        data-testid="dashboard-link-button"
      >
        {text || "Configura il bottone"}
        <ExternalLink size={20} />
      </a>
      {isAdmin && (
        <button
          onClick={() => { setForm({ text, url }); setEditing(true); }}
          className="absolute -top-2 -right-2 p-2 rounded-full transition-all duration-300 hover:scale-110"
          style={{ backgroundColor: "var(--mm-surface)", border: "1px solid var(--mm-gold)" }}
          title="Modifica bottone"
          data-testid="dashboard-button-edit-btn"
        >
          <Edit3 size={14} style={{ color: "var(--mm-gold)" }} />
        </button>
      )}
    </div>
  );
}

const CATEGORY_COLORS = {
  "Trading": "#FACC15",
  "Mindset": "#22C55E",
  "Personal Brand": "#EC4899",
  "Marketing": "#F472B6",
  "Digital Marketing": "#EC4899",
  "Rischio": "#EF4444",
  "Crescita": "#4ADE80",
  "Moneymakers": "#2FD8A8",
};

